import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Dispatch, connect } from 'react-redux';
import { IReportState, IApplicationState, ReportActionCreators, ISurgicalDeclineReportResponse } from '@nkr/store/index';
import { CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { ISurgicalDecline } from '@nkr/models/index';
import { Button, FormControl } from 'react-bootstrap';
import { HeaderWrapper } from './styles';
import Grid from './surgical-declines-grid';
import AuditReportScoreTable from '../../audit-report-score-table';
import { SurgeryDateDeclineOverlay } from '@nkr/views/center-admin/action-center/center-performance/relative-performance-overlays';
import { IRpSurgeryDateDecline } from '@nkr/models/center-performance/relative-performance';
import { LoadingIndicator } from '../../../../components/loading-indicator';

interface IOwnProps {
    centerId: number,
    isCenterLogin: boolean
}

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    getSurgicalDeclinesReport: (centerId: number, startDate: string, endDate: string) => Promise<ISurgicalDeclineReportResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    startDate: string,
    endDate: string,
    centerId: number,
    last12: boolean,
    report: ISurgicalDecline[],
    surgeryDateDecline: IRpSurgeryDateDecline,
    gridApi: GridApi,
    loading: boolean
}

class SurgicalDateDeclines extends React.Component<TProps, IState> {
    readonly state: IState = {
        startDate: '',
        endDate: '',
        centerId: 0,
        last12: false,
        report: [],
        surgeryDateDecline: null,
        gridApi: null,
        loading: false
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });

        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        let startDate = '';
        let endDate = '';

        let parsed = qs.parse(location.search);
        let last12 = parsed.last12 ? parsed.last12 == '1' : false;

        if (!last12) {
            let sd = moment().startOf('month');
            let ed = moment().endOf('month');

            startDate = sd.format('YYYY-MM-DD');
            endDate = ed.format('YYYY-MM-DD');
        } else {
            let sd = moment().add(-12, 'month');
            let ed = moment();

            startDate = sd.format('YYYY-MM-DD');
            endDate = ed.format('YYYY-MM-DD');
        }

        if (parsed.sd) {
            startDate = parsed.sd;
        }

        if (parsed.ed) {
            endDate = parsed.ed;
        }

        this.setState({
            startDate: startDate,
            endDate: endDate,
            last12: last12
        });

        this.getData(startDate, endDate);
    }

    getData = (startDate: string, endDate: string) => {
        this.setState({
            loading: true
        });

        this.props.getSurgicalDeclinesReport(this.props.centerId, startDate, endDate)
            .then((response: ISurgicalDeclineReportResponse) => {
                this.setState({
                    startDate: startDate,
                    endDate: endDate,
                    report: response.data.results.data,
                    surgeryDateDecline: response.data.results.surgeryDateDecline
                        ? {
                            rank: response.data.results.surgeryDateDecline.rank,
                            declineCount: response.data.results.surgeryDateDecline.declineCount,
                            offerCount: response.data.results.surgeryDateDecline.offerCount,
                            declineRate: response.data.results.surgeryDateDecline.declineRate,
                            category: response.data.results.surgeryDateDecline.offerCount > 0 ? 'Green' : 'NV',
                        }
                        : null
                }, () => {
                    this.setUrlParameters();
                });
            }).catch((ex) => {
                alert('An error occured while loading report data');
            })
            .finally(() => {
                this.setState({
                    loading: false
                });
            });
    }

    setUrlParameters = () => {
        let { startDate, endDate, last12 } = this.state;

        let paramList = [];

        if (startDate != '') {
            paramList.push('sd=' + startDate);
        }

        if (endDate != '') {
            paramList.push('ed=' + endDate);
        }

        if (last12) {
            paramList.push('last12=1');
        }

        let params = paramList.join('&');

        let newUrl = `/center/${this.props.centerId}/reports/my-center/surgical-date-declines?${params}`;

        if (this.props.centerId <= 0) {
            newUrl = `/report/surgical-declines?${params}`;
        }

        window.history.pushState({}, '', newUrl);
    }

    getMonths = () => {
        let monthList: { index: number, name: string, startDate: string, endDate: string }[] = [];

        for (let m = 0; m < 12; m++) {
            let sd = moment().add(-1 * m, 'month').startOf('month');
            let ed = moment().add(-1 * m, 'month').endOf('month');

            let name = sd.format('MMMM YYYY');
            let startDate = sd.format('YYYY-MM-DD');
            let endDate = ed.format('YYYY-MM-DD');

            let v = { index: m, name, startDate, endDate };

            monthList.push(v);
        }

        return monthList;
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'startDate') {
            this.setState({
                startDate: value
            });
        }

        if (name == 'endDate') {
            this.setState({
                endDate: value
            });
        }
    }

    handleDateRangeSearch = () => {
        this.getData(this.state.startDate, this.state.endDate);
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'surgical-date-declines.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.colId == 'Rank') {
                    return params.node.rowIndex + 1;
                } else if (colDef.colId == 'xpDate') {
                    return moment(params.value).format('MM/DD/YYYY');
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const surgeryDecline = this.state.surgeryDateDecline;

        return (
            <>
                <LoadingIndicator
                    type='spin'
                    color='black'
                    loading={this.state.loading}
                    message='One moment please...'
                />

                {/* {surgeryDecline && (
                    <AuditReportScoreTable
                        reportName='Surgery Date Declines - Last 12 Months'
                        scoreValue={`${surgeryDecline.declineRate}%`}
                        overlay={SurgeryDateDeclineOverlay({surgeryDateDecline: surgeryDecline})}
                    />
                )} */}

                <HeaderWrapper>
                    {this.props.isCenterLogin == false && (
                        <div className='billing-header'>
                            Surgery Date Declines Report
                        </div>
                    )}

                    <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div style={{ marginRight: 5 }}>Start Date:&nbsp;</div>
                                <div>
                                    <FormControl
                                        type='date'
                                        name='startDate'
                                        value={this.state.startDate}
                                        onChange={this.handleDateChange}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div style={{ marginRight: 5 }}>End Date:</div>
                                <div>
                                    <FormControl
                                        type='date'
                                        name='endDate'
                                        value={this.state.endDate}
                                        onChange={this.handleDateChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <Button bsStyle='primary' onClick={this.handleDateRangeSearch}>Search</Button>
                            </div>
                        </div>

                        {surgeryDecline &&
                            <AuditReportScoreTable
                                reportName={this.state.last12 ? 'Surgery Date Declines - Last 12 Months' : 'Surgery Date Declines'}
                                scoreValue={`${(+surgeryDecline.declineRate).toFixed(1)}%`}
                                overlay={SurgeryDateDeclineOverlay({ surgeryDateDecline: surgeryDecline })}
                            />
                        }

                        <div>
                            <Button onClick={this.handleExport}>Export CSV</Button>
                        </div>
                    </div>
                </HeaderWrapper>

                <Grid data={this.state.report} onGridReady={this.onGridReady} />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        getSurgicalDeclinesReport: (centerId, startDate, endDate) => {
            return dispatch(ReportActionCreators.getSurgicalDeclinesReport(centerId, startDate, endDate));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(SurgicalDateDeclines);
